/**
 * Shared utilities for control layout using flexbox.
 *
 * Used by ColumnHeaderSlot and ContentFooterSlot to provide consistent
 * sizing behavior and priority-based space distribution.
 */

import {type ControlDefinition} from '../types';

/**
 * Tailwind class names for flex sizing.
 * - flexGrow: Tailwind arbitrary value class (e.g., 'flex-[9]')
 * - minWidth: Tailwind class name (e.g., 'min-w-[180px]')
 */
export type ControlStyles = {
  flexGrow: string;
  minWidth: string;
};

export type ControlGroup = {
  name: string;
  controls: ControlDefinition[];
};

/**
 * Gets flex-grow and minWidth for a control type.
 *
 * Run and media key controls are prioritized with higher flex-grow values because
 * they typically display longer content (run names, media key paths) that benefit
 * from additional horizontal space. Step and index controls display shorter numeric
 * values and can function well with less space.
 *
 * Range sliders take full width to provide adequate horizontal space for slider
 * interaction and clear visualization of the selected range.
 *
 * @param controlType - The type of control
 * @param mode - Optional mode to apply mode-specific constraints (e.g., maxWidth only in grid/gallery)
 */
export function getControlStyles(controlType: string): ControlStyles {
  switch (controlType) {
    case 'run':
      return {flexGrow: 'flex-[9]', minWidth: 'min-w-[180px]'};
    case 'mediaKey':
      return {flexGrow: 'flex-[7]', minWidth: 'min-w-[140px]'};
    // Combined step+index group gets max 700px in grid/gallery, full width in compare
    case 'stepIndex':
      return {
        flexGrow: 'flex-[6]',
        minWidth: 'min-w-[120px]',
      };
    // Individual step or index also gets max 700px in grid/gallery, full width in compare
    case 'step':
    case 'index':
      return {
        flexGrow: 'flex-[3]',
        minWidth: 'min-w-[60px]',
      };
    case 'xAxisRange':
    case 'yAxisRange':
      return {flexGrow: 'flex-1', minWidth: 'min-w-full'};
    default:
      return {flexGrow: 'flex-1', minWidth: 'min-w-[60px]'};
  }
}

/**
 * Groups controls for flex layout.
 *
 * Step and index are grouped together because they're related navigation controls
 * that users perceive as a single unit (navigating through steps within a run).
 * Keeping them visually connected helps users understand their relationship.
 *
 * Range sliders are isolated in their own groups to force line breaks, ensuring
 * they get full horizontal space for intuitive slider interaction and clear range
 * visualization without competing for space with other controls.
 *
 * @param controls - Array of control definitions
 * @returns Array of control groups
 */
export function createControlGroups(
  controls: ControlDefinition[]
): ControlGroup[] {
  const runControl = controls.find(c => c.control === 'run');
  const mediaKeyControl = controls.find(c => c.control === 'mediaKey');
  const stepControl = controls.find(c => c.control === 'step');
  const indexControl = controls.find(c => c.control === 'index');
  const rangeControls = controls.filter(
    c => c.control === 'xAxisRange' || c.control === 'yAxisRange'
  );

  // Only group step and index if BOTH are present
  const shouldGroupStepIndex = !!(stepControl && indexControl);

  const controlGroups: ControlGroup[] = [];

  if (runControl) {
    controlGroups.push({name: 'run', controls: [runControl]});
  }
  if (mediaKeyControl) {
    controlGroups.push({name: 'mediaKey', controls: [mediaKeyControl]});
  }
  if (shouldGroupStepIndex) {
    controlGroups.push({
      name: 'stepIndex',
      controls: [stepControl!, indexControl!],
    });
  } else if (stepControl) {
    controlGroups.push({name: 'step', controls: [stepControl]});
  } else if (indexControl) {
    controlGroups.push({name: 'index', controls: [indexControl]});
  }

  // Each range slider gets its own full-width group
  rangeControls.forEach(rangeControl => {
    controlGroups.push({
      name: rangeControl.control,
      controls: [rangeControl],
    });
  });

  return controlGroups;
}
